<template>
    <div class="player-settings">
        <player-embed
            :player-embed="playerEmbed"
            :show-hash="show.hash"
            :track-no="trackNo"
            :player-type="playerType"
            :player-hash="player.hash"
        />
        <tabs-nav
            :tabs="tabs"
            :current-tab="currentTab"
            :excluded-tabs="'track' === playerType ? ['Playlist'] : []"
            @change="controlsChange"
        />
        <transition
            name="fade"
            mode="out-in"
        >
            <keep-alive>
                <component
                    :is="currentTabComponent"
                    :key="currentTabComponent"
                    :settings="settings"
                    :show="show"
                    :player-type="playerType"
                    @change="configChange"
                    @nested-config-change="nestedConfigChange"
                    @cta-change="ctaChange"
                />
            </keep-alive>
        </transition>
        <div class="button-controls">
            <base-submit-button
                class="btn is-gray"
                @click="$emit('cancel')"
            >
                Cancel
            </base-submit-button>
            <base-submit-button
                class="btn"
                :loading="formProcessing"
                @click="save"
            >
                Save Changes
            </base-submit-button>
        </div>
    </div>
</template>

<script>
import TabsNav from './TabsNav';
import dataChangeHandler from '@/utils/dataChangeHandler';
import * as settingsTabs from './PlayerSettingsTabs';
import componentName from '@/utils/componentName';
import { isEqual } from 'lodash';
import { mapActions } from 'vuex';
import toasts from '@/mixins/toasts';
import PlayerEmbed from '@/components/Embed/PlayerEmbed';

export default {
    components: {
        TabsNav,
        PlayerEmbed,
        ...settingsTabs,
    },

    mixins: [toasts],

    props: {
        show: {
            type: Object,
            required: true,
        },
        playerType: {
            type: String,
            required: true,
        },
        player: {
            type: Object,
            required: true,
        },
        playerName: {
            type: String,
            required: true,
        },
        trackNo: {
            type: String,
            default: '1:0',
        },
        playerEmbed: {
            type: [Boolean, Object],
            default: false,
        },
        initializeEmbed: {
            type: Function,
            required: true,
        },
    },

    data: (instance) => ({
        tabs: [
            {
                name: 'Player',
            },
            {
                name: 'Playlist',
            },
            {
                name: 'Colors',
            },
            {
                name: 'Call to Action',
            },
        ],
        currentTab: 'Player',
        formProcessing: false,
        settings: {
            buttonConfig: {
                cta: {
                    label: '',
                    target: '',
                    url: '',
                },
            },
            buttons: [],
            color: false,
            controls: [],
            coverDisplay: true,
            cover: {
                imageUrl: '',
                link: true,
                url: '',
            },
            sticky: false,
            name: '',
            ...('archive' === instance.playerType && {
                playlist: {
                    color: '',
                    features: [],
                    order: '',
                    displayFeaturedEpisodes: false,
                },
            }),
        },
    }),

    computed: {
        /**
         * Returns component path
         *
         * @returns  {string}  Component name
         */
        currentTabComponent() {
            return componentName(this.currentTab);
        },
    },

    watch: {
        player: {
            immediate: true,

            /**
             * Manage data.
             *
             * @param {object} newSettings Player object data.
             * @param {*} oldSettings Player object data.
             */
            handler(newSettings, oldSettings) {
                if (isEqual(newSettings, oldSettings)) {
                    return;
                }

                this.settings = {
                    ...this.settings,
                    ...newSettings,
                };
            },
        },

        playerName: {
            immediate: true,

            /**
             * Manage data.
             *
             * @param {string} newSetting Player name.
             * @param {*} oldSetting Player name.
             */
            handler(newSetting, oldSetting) {
                if (newSetting === oldSetting) {
                    return;
                }

                this.settings.name = newSetting;
            },
        },

        settings: {
            immediate: true,
            deep: true,

            /**
             * @param {object} newSettings  New settings object.
             */
            handler(newSettings) {
                this.$emit('set-embed-config', this.settings);
            },
        },
    },

    /**
     * Created hook
     */
    created() {
        this.$emit('set-embed-config', this.settings);
        this.initializeEmbed();
    },

    methods: {
        ...mapActions([
            'updatePlayer',
            'updateTrackPlayerConfig',
        ]),

        /**
         * Handle controls change
         *
         * @param   {object}  changed  Changed configuation
         *
         */
        controlsChange(changed) {
            dataChangeHandler(this, changed);
        },

        /**
         * Handle controls change
         *
         * @param   {object}  data  Changed configuation
         * @param   {string}  data.parent Parent property
         * @param   {object}    data.data   New config
         *
         */
        nestedConfigChange({ parent, data }) {
            this.settings[parent] = {
                ...this.settings[parent],
                ...data,
            };
        },

        /**
         * Handle CTA change
         *
         * @param   {object}  data  Config data
         *
         */
        ctaChange(data) {
            this.settings.buttonConfig.cta = {
                ...this.settings.buttonConfig.cta,
                ...data,
            };
        },

        /**
         * Handle config change
         *
         * @param   {object}  changed  Object with configuration
         *
         */
        configChange(changed) {
            for (const key in changed) {
                this.settings[key] = changed[key];
            }
        },

        /**
         * Save new player settings
         *
         */
        async save() {
            if (! this.isValid() || this.formProcessing) {
                return;
            }

            this.formProcessing = ! this.formProcessing;

            try {
                if ('archive' === this.playerType) {
                    await this.updatePlayer({
                        type: 'archive',
                        hash: this.player.hash,
                        showHash: this.show.hash,
                        playerData: this.settings,
                    });
                } else {
                    await this.updateTrackPlayerConfig({
                        type: 'track',
                        showHash: this.show.hash,
                        trackNo: this.trackNo,
                        playerData: this.configureTrackData(this.settings),
                    });
                }

                this.toastSuccess({
                    message: {
                        title: 'Your Player settings has been saved!',
                    },
                });

                this.$emit('player-changed', this.settings);
            } catch (e) {
                this.errorMessage('We couldn\'t update your player. Please try again later or contact us.');
            }

            this.formProcessing = ! this.formProcessing;
        },

        /**
         * Validate required fields
         *
         * @returns  {boolean}  If all required fields are entered
         */
        isValid() {
            if (
                '' === this.settings.buttonConfig.cta.url &&
                this.settings.buttons.includes('cta')
            ) {
                this.errorMessage('Please fill CTA Button URL');

                return false;
            }

            return true;
        },

        /**
         * Removes redundant track player config values
         *
         * @param   {object}  data  Config data
         *
         * @returns  {object}        Pure config data.
         */
        configureTrackData(data) {
            const trackData = { ...data };

            delete trackData.name;
            delete trackData.updatedAt;

            return trackData;
        },

        /**
         * Displays error toast
         *
         * @param   {string}  title  Error message
         *
         */
        errorMessage(title) {
            this.toastError({ message: { title } });
        },
    },
};
</script>
